"use client";

import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { HelpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type FeatureIntroHeaderProps = {
  icon: LucideIcon;
  label: string;
  title: string;
  description: React.ReactNode;
  /** Full dialog content rendered inside a pre-styled DialogContent shell. */
  dialogContent?: React.ReactNode;
  /** Label for the inline dialog trigger link. Defaults to "How it works". */
  dialogTriggerLabel?: string;
  /** Slot rendered at the far right (e.g. action buttons). */
  trailing?: React.ReactNode;
  className?: string;
};

export function FeatureIntroHeader({
  icon: Icon,
  label,
  title,
  description,
  dialogContent,
  dialogTriggerLabel = "How it works",
  trailing,
  className,
}: FeatureIntroHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-4 border-b border-border/60 pb-4",
        className
      )}
    >
      {/* Left: icon + text */}
      <div className="flex items-start gap-3 min-w-0">
        <div className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 mt-0.5">
          <Icon className="h-5 w-5 text-primary" aria-hidden />
        </div>

        <div className="min-w-0">
          <span className="inline-block text-[10px] font-semibold tracking-wide text-primary bg-primary/8 rounded-full px-2 py-0.5 mb-1.5 leading-none">
            {label}
          </span>
          <p className="text-base font-semibold text-foreground leading-tight">
            {title}
          </p>
          <p className="mt-1 text-xs text-muted-foreground leading-snug">
            {description}
            {dialogContent && (
              <>
                {" "}
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="inline-flex items-center gap-1 text-primary font-semibold hover:underline underline-offset-2 focus:outline-none whitespace-nowrap">
                      {dialogTriggerLabel}
                      <HelpCircle className="h-3 w-3" />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-xl p-0 gap-0" mobileFullScreen>
                    {dialogContent}
                  </DialogContent>
                </Dialog>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Right: actions */}
      {trailing && (
        <div className="shrink-0 flex items-center gap-2 mt-0.5">{trailing}</div>
      )}
    </div>
  );
}
